import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/helm-framework", label: "Helm Framework" },
    { to: "/ai-opportunity-sprint", label: "AI Opportunity Sprint" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`sticky top-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "bg-offwhite/95 backdrop-blur-md border-b border-sand"
                    : "bg-offwhite/80 backdrop-blur-sm border-b border-transparent"
            }`}
        >
            <div className="cap-container flex items-center justify-between py-5">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="group flex items-center gap-3.5 md:gap-4"
                >
                    <span className="h-10 w-10 md:h-11 md:w-11 border border-navy flex items-center justify-center font-serif text-navy text-2xl leading-none">
                        C
                    </span>
                    <span className="flex flex-col leading-tight">
                        <span className="font-serif text-navy text-xl md:text-2xl tracking-tight" style={{ fontWeight: 600 }}>
                            Chief AI Partners
                        </span>
                        <span
                            className="text-[11px] md:text-[11.5px] uppercase tracking-[0.28em] text-navy/70 mt-0.5"
                            style={{ fontFamily: "Manrope", fontWeight: 600 }}
                        >
                            Fractional AI Leadership
                        </span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-[0.95rem] font-semibold tracking-tight pb-1 border-b transition-colors ${
                                    isActive
                                        ? "text-navy border-navy"
                                        : "text-slate border-transparent hover:text-navy hover:border-navy/40"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="nav-book-consultation-btn"
                        className="cap-btn-primary text-[0.9rem] px-6 py-3 ml-3"
                    >
                        Book a 15-minute conversation
                    </Link>
                </nav>

                <button
                    className="lg:hidden p-2 text-navy"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="mobile-nav-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    className="lg:hidden border-t border-sand bg-offwhite"
                    data-testid="mobile-nav-menu"
                >
                    <nav className="cap-container flex flex-col py-6 gap-4">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-base font-medium py-1 ${
                                        isActive ? "text-navy" : "text-slate"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="mobile-nav-book-btn"
                            className="cap-btn-primary mt-2"
                        >
                            Book a 15-minute conversation
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
}
